import React, { useEffect, useMemo, useState } from "react";
import { Page } from "../../layout/page";
import {
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";

const MODULE_KEYS = [
  { labelKey: "accounting.modules.chartOfAccounts.label", descKey: "accounting.modules.chartOfAccounts.description", path: "chartOfAccounts" },
  { labelKey: "accounting.modules.journalEntries.label", descKey: "accounting.modules.journalEntries.description", path: "journalEntries" },
  { labelKey: "accounting.modules.fiscalPeriods.label", descKey: "accounting.modules.fiscalPeriods.description", path: "fiscalPeriods" },
  { labelKey: "accounting.modules.trialBalance.label", descKey: "accounting.modules.trialBalance.description", path: "trialBalance" },
  { labelKey: "accounting.modules.financialStatements.label", descKey: "accounting.modules.financialStatements.description", path: "financialStatements" },
  { labelKey: "accounting.modules.subledgerReconciliation.label", descKey: "accounting.modules.subledgerReconciliation.description", path: "subledgerReconciliation" },
  { labelKey: "accounting.modules.postingRules.label", descKey: "accounting.modules.postingRules.description", path: "postingRules" },
  { labelKey: "accounting.modules.openingBalance.label", descKey: "accounting.modules.openingBalance.description", path: "openingBalance" },
  { labelKey: "accounting.modules.financeReports.label", descKey: "accounting.modules.financeReports.description", path: "report", search: "?tab=finance" },
];

const AccountingHome = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [initializing, setInitializing] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);

  const basePath = location.pathname.startsWith("/management")
    ? "/management"
    : "/account";

  const links = useMemo(
    () =>
      MODULE_KEYS.map((item) => ({
        to: `${basePath}/${item.path}`,
        search: item.search || "",
        label: t(item.labelKey),
        description: t(item.descKey),
      })),
    [basePath, t]
  );

  const load = async () => {
    const data = await api.Accounting.getCurrentFiscalPeriod();
    setCurrentPeriod(data?.period || null);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      await api.Accounting.initialize();
      await load();
      showNotification({
        title: t("accounting.home.initializedTitle"),
        message: t("accounting.home.initializedMessage"),
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.home.initializeFailed"),
        message: String(error),
      });
    } finally {
      setInitializing(false);
    }
  };

  const openModule = (link) => {
    tabNav.open({
      pathname: link.to,
      search: link.search,
      label: link.label,
    });
  };

  return (
    <Page title={t("Accounting")}>
      <Stack>
        <Card withBorder>
          <Group position="apart">
            <div>
              <Title order={4}>{t("accounting.home.glTitle")}</Title>
              <Text size="sm" color="dimmed">
                {t("accounting.home.currentPeriod", {
                  period: currentPeriod?.name || t("accounting.home.notSet"),
                })}
              </Text>
              <Text size="xs" color="dimmed" mt={4}>
                {t("accounting.home.intro")}
              </Text>
            </div>
            <Button loading={initializing} onClick={handleInitialize}>
              {t("accounting.home.initialize")}
            </Button>
          </Group>
        </Card>

        <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          {links.map((link) => (
            <Card
              key={link.to}
              withBorder
              padding="md"
              onClick={() => openModule(link)}
              sx={(theme) => ({
                cursor: "pointer",
                transition: "border-color 150ms ease",
                "&:hover": {
                  borderColor:
                    theme.colors[theme.primaryColor][
                      theme.fn.primaryShade(
                        theme.colorScheme === "dark" ? 4 : 6
                      )
                    ],
                },
              })}
            >
              <Stack spacing={4}>
                <Text weight={600}>{link.label}</Text>
                <Text size="sm" color="dimmed" sx={{ lineHeight: 1.5 }}>
                  {link.description}
                </Text>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>
    </Page>
  );
};

export default AccountingHome;
